/**
 * Copyright (c) 2026 Spicy Friends Ltd.
 * All rights reserved.
 *
 * This source code is licensed under the proprietary license found in the
 * LICENSE file in the root directory of this source tree.
 */

import { NetworkStatus } from '@apollo/client'
import { type QueryRef, useApolloClient, useReadQuery } from '@apollo/client/react'
import { ErrorAlert, SearchExpand } from '@repo/shared-client/components/blocks'
import { NativeSelectFormField } from '@repo/shared-client/components/fields'
import { Button, Card, CardContent, Separator, Tabs, TabsList, TabsTrigger } from '@repo/shared-client/components/ui'
import { Form, useForm } from '@repo/shared-client/form'
import { createFileRoute, stripSearchParams, useNavigate, useSearch } from '@tanstack/react-router'
import AdminPaymentAccepted, {
  PAYMENTS_APPROVED_LIST_QUERY,
} from '@web/src/features/admin/admin-payment-accepted/admin-payment-accepted'
import AdminPaymentRequests, {
  PAYMENT_REQUESTS_LIST_QUERY,
} from '@web/src/features/admin/admin-payment-requests/admin-payment-requests'
import {
  ADMIN_PAYMENTS_SUMMARY_QUERY,
  AdminPaymentsSummaryStrip,
} from '@web/src/features/admin/admin-payments/admin-payments-summary'
import { queryAndPreload } from '@web/src/lib/apollo/query-and-preload'
import { env } from '@web/src/lib/env.client'
import { useTranslation } from '@web/src/lib/intl/react'
import { robotsMetaForEnvironment, seo } from '@web/src/lib/seo'
import { PlusIcon } from 'lucide-react'
import { useCallback } from 'react'
import SFPageLoading from 'src/components/primatives/sf-page-loading/sf-page-loading'
import type {
  AdminPaymentsSummaryQuery,
  PaymentApprovedSortEnum,
  PaymentRequestSortEnum,
  PaymentRequestsListQuery,
  PaymentsApprovedListQuery,
} from 'types/graphql'
import { z } from 'zod'

const paymentTabs = ['pending', 'approved', 'refunded'] as const
type PaymentTab = (typeof paymentTabs)[number]

const paymentsSearchSchema = z.object({
  tab: z.enum(paymentTabs).optional(),
  page: z.number().optional(),
  sortBy: z.string().optional(),
  search: z.string().optional(),
  profileId: z.string().optional(),
})

export const Route = createFileRoute('/(auth)/(admin-layout)/admin/payments/')({
  component: PaymentsPage,
  validateSearch: paymentsSearchSchema,
  search: {
    middlewares: [
      stripSearchParams({
        tab: 'pending',
        page: 1,
        sortBy: 'LATEST',
        search: '',
      }),
    ],
  },
  loaderDeps: ({ search }) => ({
    tab: search.tab ?? 'pending',
    page: search.page ?? 1,
    sortBy: search.sortBy ?? 'LATEST',
    search: search.search ?? '',
    profileId: search.profileId,
  }),
  loader: async ({ context, deps }) => {
    const summary = await queryAndPreload(context, ADMIN_PAYMENTS_SUMMARY_QUERY)
    const filter = listFilter(deps.search, deps.profileId)

    if (deps.tab === 'pending') {
      const list = await queryAndPreload(context, PAYMENT_REQUESTS_LIST_QUERY, {
        variables: {
          page: deps.page,
          filter,
          sortBy: deps.sortBy as PaymentRequestSortEnum,
        },
      })
      return {
        tab: 'pending' as const,
        summaryQueryRef: summary.queryRef,
        requestsQueryRef: list.queryRef,
        approvedQueryRef: null,
      }
    }

    const list = await queryAndPreload(context, PAYMENTS_APPROVED_LIST_QUERY, {
      variables: {
        page: deps.page,
        filter: {
          ...filter,
          refunded: deps.tab === 'refunded',
        },
        sortBy: deps.sortBy as PaymentApprovedSortEnum,
      },
    })
    return {
      tab: deps.tab,
      summaryQueryRef: summary.queryRef,
      requestsQueryRef: null,
      approvedQueryRef: list.queryRef,
    }
  },
  head: () =>
    seo({
      title: `Admin - Payments | ${env.VITE_APP_NAME}`,
      description: 'Admin payments',
      robots: robotsMetaForEnvironment({ all: 'noindex,nofollow' }),
    }),
})

function listFilter(search: string, profileId: string | undefined) {
  const filter: { search?: string; profileId?: string } = {}
  if (search) {
    filter.search = search
  }
  if (profileId) {
    filter.profileId = profileId
  }
  return Object.keys(filter).length > 0 ? filter : undefined
}

function usePaymentParams() {
  const search = useSearch({ strict: false })
  const tab = paymentTabs.includes(search.tab as PaymentTab) ? (search.tab as PaymentTab) : 'pending'
  return {
    tab,
    page: search.page || 1,
    sortBy: search.sortBy || 'LATEST',
    search: search.search || '',
    profileId: search.profileId || undefined,
  }
}

function PaymentsPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const client = useApolloClient()
  const params = usePaymentParams()
  const { summaryQueryRef, requestsQueryRef, approvedQueryRef } = Route.useLoaderData()
  const summaryResult = useReadQuery(summaryQueryRef as QueryRef<AdminPaymentsSummaryQuery>)

  const formMethods = useForm({
    defaultValues: {
      sortBy: params.sortBy,
      search: params.search,
    },
  })

  const handleNavigate = useCallback(
    (updates: { page?: number; sortBy?: string; search?: string; tab?: PaymentTab; profileId?: string }) => {
      const nextSearch = 'search' in updates ? updates.search || undefined : params.search || undefined
      const nextProfileId = 'profileId' in updates ? updates.profileId : params.profileId
      navigate({
        to: '/admin/payments',
        search: {
          tab: updates.tab ?? params.tab,
          page: updates.page ?? params.page,
          sortBy: updates.sortBy ?? params.sortBy,
          search: nextSearch,
          profileId: nextProfileId,
        },
      } as never)
    },
    [navigate, params.page, params.profileId, params.search, params.sortBy, params.tab]
  )

  const refetchLists = useCallback(() => {
    const filter = listFilter(params.search, params.profileId)
    const listPromise =
      params.tab === 'pending'
        ? client.query({
            query: PAYMENT_REQUESTS_LIST_QUERY,
            variables: {
              page: params.page,
              filter,
              sortBy: params.sortBy as PaymentRequestSortEnum,
            },
            fetchPolicy: 'network-only',
          })
        : client.query({
            query: PAYMENTS_APPROVED_LIST_QUERY,
            variables: {
              page: params.page,
              filter: { ...filter, refunded: params.tab === 'refunded' },
              sortBy: params.sortBy as PaymentApprovedSortEnum,
            },
            fetchPolicy: 'network-only',
          })
    const summaryPromise = client.query({
      query: ADMIN_PAYMENTS_SUMMARY_QUERY,
      fetchPolicy: 'network-only',
    })
    return Promise.all([listPromise, summaryPromise])
  }, [client, params.page, params.profileId, params.search, params.sortBy, params.tab])

  const handleTabChange = (value: string) => {
    if (!paymentTabs.includes(value as PaymentTab) || value === params.tab) {
      return
    }
    handleNavigate({ tab: value as PaymentTab, page: 1 })
  }

  if (summaryResult.networkStatus === NetworkStatus.loading) {
    return <SFPageLoading />
  }
  if (summaryResult.error) {
    return <ErrorAlert error={summaryResult.error} />
  }

  return (
    <div className="mx-auto w-full max-w-screen-2xl px-2 py-4 md:px-4">
      <div className="flex w-full flex-col gap-4">
        <h1 className="font-semibold text-2xl tracking-tight">{t('Payments')}</h1>
        <AdminPaymentsSummaryStrip summary={summaryResult.data?.adminPaymentsSummary} />
        <Tabs onValueChange={handleTabChange} value={params.tab}>
          <TabsList className="w-full justify-start overflow-x-auto overflow-y-hidden" variant="tabs">
            <TabsTrigger value="pending">{t('Pending')}</TabsTrigger>
            <TabsTrigger value="approved">{t('Approved')}</TabsTrigger>
            <TabsTrigger value="refunded">{t('Refunded')}</TabsTrigger>
          </TabsList>
        </Tabs>
        {params.profileId ? (
          <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-border px-3 py-2 text-sm">
            <span>{t('Filtered to one profile')}</span>
            <Button
              onClick={() => handleNavigate({ profileId: undefined, page: 1 })}
              size="sm"
              type="button"
              variant="outline"
            >
              {t('Clear profile filter')}
            </Button>
          </div>
        ) : null}
        <Form
          className="w-full"
          formMethods={formMethods}
          onSubmit={(data) => {
            const searchVal = String(data.search ?? '').trim()
            handleNavigate({
              page: 1,
              sortBy: data.sortBy,
              search: searchVal || undefined,
            })
          }}
        >
          <SearchExpand
            className="w-full"
            clearSearchLabel={t('Clear search')}
            debounceTime={300}
            fieldProps={{ className: 'min-w-0 flex-1' }}
            inputProps={{ 'aria-label': t('Search payments') }}
            minChars={0}
            name="search"
            onSearchChange={(value) => {
              handleNavigate({ search: value.trim() || undefined, page: 1 })
            }}
            placeholder={t('Search by ID, name, or email')}
            rightBarChildren={
              params.tab === 'pending' ? (
                <Button
                  aria-label={t('Create payment request')}
                  onClick={() => navigate({ to: '/admin/payments/create' })}
                  size="icon"
                  type="button"
                >
                  <PlusIcon />
                </Button>
              ) : null
            }
            toggleFiltersLabel={t('Toggle filters')}
          >
            <Card className="gap-0 py-0">
              <CardContent className="px-2 py-3">
                <NativeSelectFormField
                  fieldProps={{ className: 'min-w-0 w-full sm:min-w-[10rem]' }}
                  label={t('Sort By')}
                  name="sortBy"
                  onValueChange={() => handleNavigate({ sortBy: formMethods.getValues('sortBy') })}
                >
                  <option value="LATEST">{t('Latest')}</option>
                  <option value="OLDEST">{t('Oldest')}</option>
                </NativeSelectFormField>
              </CardContent>
            </Card>
          </SearchExpand>
        </Form>
        <Separator className="w-full" />
        {requestsQueryRef ? (
          <PendingPaymentsList page={params.page} queryRef={requestsQueryRef} refetch={refetchLists} />
        ) : null}
        {approvedQueryRef ? (
          <ApprovedPaymentsList
            emptyTitle={params.tab === 'refunded' ? t('No refunded payments found') : undefined}
            page={params.page}
            queryRef={approvedQueryRef}
            refetch={refetchLists}
          />
        ) : null}
      </div>
    </div>
  )
}

function PendingPaymentsList({
  queryRef,
  page,
  refetch,
}: {
  queryRef: unknown
  page: number
  refetch: () => Promise<unknown>
}) {
  const { data, error, networkStatus } = useReadQuery(queryRef as QueryRef<PaymentRequestsListQuery>)
  if (networkStatus === NetworkStatus.loading) {
    return <SFPageLoading />
  }
  if (error) {
    return <ErrorAlert error={error} />
  }
  return <AdminPaymentRequests page={page} paymentRequestsListPage={data?.paymentRequestsListPage} refetch={refetch} />
}

function ApprovedPaymentsList({
  queryRef,
  page,
  refetch,
  emptyTitle,
}: {
  queryRef: unknown
  page: number
  refetch: () => Promise<unknown>
  emptyTitle?: string
}) {
  const { data, error, networkStatus } = useReadQuery(queryRef as QueryRef<PaymentsApprovedListQuery>)
  if (networkStatus === NetworkStatus.loading) {
    return <SFPageLoading />
  }
  if (error) {
    return <ErrorAlert error={error} />
  }
  return (
    <AdminPaymentAccepted
      emptyTitle={emptyTitle}
      page={page}
      paymentsApprovedListPage={data?.paymentsApprovedListPage}
      refetch={refetch}
    />
  )
}
